import React from 'react';
import { EMZI_COLORS, EMZI_RADIUS, EMZI_SPACING } from '../../styles/emziCI';
import StructuredListInput from './StructuredListInput';
import KeyValuePairInput from './KeyValuePairInput';
import { BILINGUAL_REQUIRED } from '../../utils/formValidation';

/**
 * EnhancedHealthForm - Health pack form with Input Quality Layer enhancements
 * Focus: Health claims, benefits, medical information (BILINGUAL - critical for accuracy)
 */
const EnhancedHealthForm = ({ 
  content = {}, 
  onChange, 
  errors = {}, 
  touched = {},
  onFieldBlur,
  packType = 'health'
}) => {
  const handleChange = (field, value) => {
    onChange({ ...content, [field]: value });
  };

  const requiresBilingual = BILINGUAL_REQUIRED[packType];

  return (
    <div style={{ 
      display: 'flex',
      flexDirection: 'column',
      gap: EMZI_SPACING.md,
    }}>
      {/* Critical Health Info Notice */}
      <div style={{
        padding: EMZI_SPACING.sm,
        background: '#FEF2F2',
        borderRadius: EMZI_RADIUS.sm,
        fontSize: '12px',
        color: EMZI_COLORS.error,
        borderLeft: `3px solid ${EMZI_COLORS.error}`,
      }}>
        ⚠️ <strong>Critical Health Information:</strong> Health packs contain medical/health claims. Bilingual (EN & MS) required for accuracy. All claims must be verified.
      </div>

      {/* Health Claim Category */}
      <div style={{ marginBottom: EMZI_SPACING.md }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: EMZI_SPACING.xs,
          color: EMZI_COLORS.textPrimary,
        }}>
          Health Claim Category
          <span style={{ color: EMZI_COLORS.error, marginLeft: '4px' }}>*</span>
        </label>
        
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          💡 Type of health claim. Example: "Cardiovascular Support" or "Cholesterol Management" or "Blood Pressure Support"
        </div>

        <input
          type="text"
          value={content.health_category || ''}
          onChange={(e) => handleChange('health_category', e.target.value)}
          onBlur={() => onFieldBlur && onFieldBlur('health_category')}
          placeholder="e.g., Cardiovascular Support, Cholesterol Management, Blood Pressure Support"
          style={{
            width: '100%',
            padding: EMZI_SPACING.sm,
            border: `1px solid ${touched.health_category && errors.content?.health_category ? EMZI_COLORS.error : EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            outline: 'none',
          }}
        />
        {touched.health_category && errors.content?.health_category && (
          <div style={{
            marginTop: '4px',
            fontSize: '12px',
            color: EMZI_COLORS.error,
          }}>
            {errors.content.health_category}
          </div>
        )}
      </div>

      {/* Approved Health Claims */}
      <StructuredListInput
        label="Approved Health Claims"
        items={content.approved_claims || []}
        onChange={(items) => handleChange('approved_claims', items)}
        minItems={1}
        maxItems={10}
        placeholder="Add an approved claim..."
        helper="Verified health claims agents CAN make. Example: 'Improves circulation and heart function' or 'Supports healthy cholesterol levels'"
        required={true}
        hasError={touched.approved_claims && errors.content?.approved_claims}
        errorMessage={errors.content?.approved_claims}
      />

      {/* Prohibited Health Claims */}
      <StructuredListInput
        label="Prohibited Health Claims"
        items={content.prohibited_claims || []}
        onChange={(items) => handleChange('prohibited_claims', items)}
        minItems={1}
        maxItems={15}
        placeholder="Add a prohibited claim..."
        helper="Claims agents MUST NOT make. Example: 'Cures heart disease' or 'Eliminates cholesterol' or 'No medical claims or disease cure statements'"
        required={true}
        hasError={touched.prohibited_claims && errors.content?.prohibited_claims}
        errorMessage={errors.content?.prohibited_claims}
      />

      {/* Safe Alternative Phrasing */}
      <KeyValuePairInput
        label="Safe Alternative Phrasing (Prohibited → Safe)"
        pairs={content.safe_phrasing || {}}
        onChange={(pairs) => handleChange('safe_phrasing', pairs)}
        keyLabel="Prohibited phrase"
        valueLabel="Safe alternative"
        helper="How to rephrase risky claims safely. Example: 'Cures heart disease' → 'Formulated to support healthy heart function and circulation when used as part of a balanced lifestyle'"
        required={true}
      />

      {/* Scientific Backing */}
      <StructuredListInput
        label="Scientific Backing/Evidence"
        items={content.scientific_evidence || []}
        onChange={(items) => handleChange('scientific_evidence', items)}
        minItems={0}
        maxItems={8}
        placeholder="Add evidence source..."
        helper="Supporting research or certifications. Example: 'Olive leaf extract research from European Journal of Nutrition 2024' or 'BPFK Malaysia registered supplement'"
        required={false}
        hasError={false}
      />

      {/* Target Health Conditions */}
      <StructuredListInput
        label="Target Health Conditions"
        items={content.target_conditions || []}
        onChange={(items) => handleChange('target_conditions', items)}
        minItems={0}
        maxItems={10}
        placeholder="Add a condition..."
        helper="What health issues this addresses. Example: 'High cholesterol' or 'Weak immune system' or 'Digestive discomfort'"
        required={false}
        hasError={false}
      />

      {/* Recommended Usage */}
      <div style={{ marginBottom: EMZI_SPACING.md }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: EMZI_SPACING.xs,
          color: EMZI_COLORS.textPrimary,
        }}>
          Recommended Usage
          <span style={{
            fontSize: '11px',
            color: EMZI_COLORS.textMuted,
            marginLeft: EMZI_SPACING.xs,
            fontWeight: '400',
          }}>
            (Optional)
          </span>
        </label>
        
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          💡 How to use for optimal health benefits. Example: "Take 1 capsule twice daily after meals with water for best absorption"
        </div>

        <textarea
          value={content.recommended_usage || ''}
          onChange={(e) => handleChange('recommended_usage', e.target.value)}
          placeholder="e.g., Take 2 capsules daily with meals for 3 months minimum"
          rows={2}
          style={{
            width: '100%',
            padding: EMZI_SPACING.sm,
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontFamily: 'inherit',
            outline: 'none',
            resize: 'vertical',
          }}
        />
      </div>

      {/* Contraindications/Warnings */}
      <StructuredListInput
        label="Contraindications/Warnings"
        items={content.warnings || []}
        onChange={(items) => handleChange('warnings', items)}
        minItems={0}
        maxItems={10}
        placeholder="Add a warning..."
        helper="Safety warnings. Example: 'Not suitable for pregnant women' or 'Consult doctor if on blood thinners' or 'May cause drowsiness'"
        required={false}
        hasError={false}
      />

      {/* Side Effects */}
      <StructuredListInput
        label="Possible Side Effects"
        items={content.side_effects || []}
        onChange={(items) => handleChange('side_effects', items)}
        minItems={0}
        maxItems={10}
        placeholder="Add a side effect..."
        helper="Known side effects agents should disclose. Example: 'Mild stomach upset in some users' or 'Rare allergic reactions'"
        required={false}
        hasError={false}
      />

      {/* Disclaimers */}
      <StructuredListInput
        label="Required Disclaimers"
        items={content.disclaimers || []}
        onChange={(items) => handleChange('disclaimers', items)}
        minItems={0}
        maxItems={8}
        placeholder="Add a disclaimer..."
        helper="Legal disclaimers. Example: 'This product is not intended to diagnose, treat, cure, or prevent any disease' or 'Individual results may vary'"
        required={false}
        hasError={false}
      />

      {/* Regulatory Compliance */}
      <div style={{ marginBottom: EMZI_SPACING.md }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: EMZI_SPACING.xs,
          color: EMZI_COLORS.textPrimary,
        }}>
          Regulatory Compliance
          <span style={{
            fontSize: '11px',
            color: EMZI_COLORS.textMuted,
            marginLeft: EMZI_SPACING.xs,
            fontWeight: '400',
          }}>
            (Optional)
          </span>
        </label>
        
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          💡 Regulatory status. Example: "Registered with BPFK Malaysia (MAL12345678)" or "GMP certified facility"
        </div>

        <textarea
          value={content.regulatory_compliance || ''}
          onChange={(e) => handleChange('regulatory_compliance', e.target.value)}
          placeholder="e.g., Registered with BPFK Malaysia (MAL12345678)"
          rows={2}
          style={{
            width: '100%',
            padding: EMZI_SPACING.sm,
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontFamily: 'inherit',
            outline: 'none',
            resize: 'vertical',
          }}
        />
      </div>

      {/* Critical warning */}
      <div style={{
        padding: EMZI_SPACING.sm,
        background: '#FEF2F2',
        borderRadius: EMZI_RADIUS.sm,
        fontSize: '12px',
        color: EMZI_COLORS.error,
        borderLeft: `3px solid ${EMZI_COLORS.error}`,
      }}>
        ⚠️ <strong>Medical Safety:</strong> All health claims must be verified by qualified professionals. Never allow agents to make unsubstantiated medical claims. Always include required disclaimers.
      </div>
    </div>
  );
};

export default EnhancedHealthForm;
